import { useEffect, useState } from "react";
import { useAccount, useWalletClient } from "wagmi";
import {
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import NoRowsOverlay from "../NoRowsOverlay";
import { MuiTableLoading } from "../MuiTableLoading";
import { accountColumns } from "./accountColumns";
import { accountRow, OfferType } from "./accountRows";
import AccountTableRow from "./accountTableRow";
import { useGetOffersWithBalance } from "../../../services/useGetOffersWithBalance";

const AccountTable = ({
  fetcher,
  eventType = false,
  interestRate = false,
  to = false,
  action = () => <></>,
  collapsible = false,
  checkBalance = false,
}: {
  fetcher: (props: any) => Promise<any>;
  eventType?: boolean;
  interestRate?: boolean;
  to?: boolean;
  action?: (offer: OfferType, refresh: () => void) => JSX.Element;
  collapsible?: boolean;
  checkBalance?: boolean;
}) => {
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOffers, setTotalOffers] = useState(0);

  const [offers, setOffers] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);

  const [loading, setLoading] = useState(true);

  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const refreshOffers = async () => {
    try {
      if (!isConnected || !address) return;
      setLoading(true);
      const res = await fetcher({
        limit: rowsPerPage,
        page,
        address,
      });
      setTotalPages(res.totalPages);
      setTotalOffers(res.totalCount);
      setOffers(res.results);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshOffers();
  }, [page, address, isConnected, rowsPerPage]);

  useEffect(() => {
    if (refresh) {
      refreshOffers();
      setRefresh(false);
    }
  }, [refresh]);

  const offersWithBalance = useGetOffersWithBalance({
    lendOffers: offers,
    walletClient,
    checkBalance,
  });

  const columns = accountColumns({ eventType, interestRate, to, collapsible });

  const rows = (offersWithBalance as OfferType[])?.map((offer) => {
    return accountRow({
      offer,
      eventType,
      interestRate,
      to,
      action: action(offer, () => setRefresh(true)),
    });
  });

  return (
    <>
      {/* <!-- Offers --> */}
      <TableContainer className="scrollbar-custom">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  {...(column.align
                    ? { align: column.align as TableCellProps["align"] }
                    : {})}
                  style={{ minWidth: column.minWidth }}
                  classes={{
                    root: "dark:bg-jacarta-500 bg-light-base dark:text-jacarta-100 text-jacarta-700",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows &&
              rows.length > 0 &&
              (!loading ? (
                rows.map((row, index) => {
                  return (
                    <AccountTableRow columns={columns} row={row} key={index} />
                  );
                })
              ) : (
                <MuiTableLoading columnLength={columns.length} />
              ))}
            {!offers ||
              (offers.length === 0 &&
                (loading ? (
                  <MuiTableLoading columnLength={columns.length} />
                ) : (
                  <NoRowsOverlay columnLength={columns.length} />
                )))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: "dark:bg-jacarta-700 bg-light-base dark:text-jacarta-100 text-jacarta-700 flex flex-row-reverse",
        }}
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalOffers || rowsPerPage * totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        backIconButtonProps={{
          classes: {
            root: `dark:bg-jacarta-700 bg-light-base dark:text-jacarta-100 text-jacarta-700 ${
              !loading && (!offers.length || page === 0) ? "hidden" : ""
            } ${loading ? "hidden" : ""}`,
          },
        }}
        nextIconButtonProps={{
          classes: {
            root: `dark:bg-jacarta-700 bg-light-base dark:text-jacarta-100 text-jacarta-700 ${
              !loading &&
              (!offers.length ||
                offers.length < rowsPerPage ||
                page + 1 === totalPages)
                ? "hidden"
                : ""
            } ${loading ? "hidden" : ""}`,
          },
        }}
        labelRowsPerPage="Offers per page"
      />
    </>
  );
};

export default AccountTable;
